import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Mail, Send, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { LandingHeader } from "@/components/LandingHeader";
import { Footer } from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#EAE8E4] font-sans text-slate-900 antialiased">
      <LandingHeader />

      <main className="flex-1 py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-12 group"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Retour à l'accueil
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">

            {/* Left — info */}
            <div className="lg:sticky lg:top-24">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-6">
                Contact
              </p>
              <h1 className="font-serif text-5xl lg:text-6xl text-slate-900 leading-tight mb-8">
                Parlons de votre{" "}
                <em className="not-italic text-emerald-700">besoin.</em>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed font-light mb-12">
                Une question, un besoin d'accompagnement, ou simplement l'envie
                d'en savoir plus ? Notre équipe est à votre écoute.
              </p>

              <div className="flex items-start gap-5">
                <div className="h-12 w-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Mail className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900 mb-1">Email</div>
                  <a
                    href="mailto:contact@hafani-ms.com"
                    className="text-emerald-700 hover:text-emerald-800 font-medium transition-colors"
                  >
                    contact@hafani-ms.com
                  </a>
                  <p className="text-sm text-slate-500 mt-1 font-light">Réponse sous 24h ouvrées.</p>
                </div>
              </div>
            </div>

            {/* Right — form */}
            <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-sm border border-slate-100">
              {sent ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h2 className="font-serif text-2xl text-slate-900 mb-3">Message envoyé !</h2>
                  <p className="text-slate-500 max-w-xs font-light">
                    Nous reviendrons vers vous dans les meilleurs délais.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        Nom complet <span className="text-red-400 normal-case">*</span>
                      </Label>
                      <Input
                        id="name" name="name" required
                        placeholder="Mohamed Mssahazi"
                        value={form.name} onChange={handleChange}
                        className="h-12 rounded-xl border-slate-200 focus-visible:ring-emerald-500 text-sm bg-slate-50"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        Email <span className="text-red-400 normal-case">*</span>
                      </Label>
                      <Input
                        id="email" name="email" type="email" required
                        placeholder="mssahazi@exemple.com"
                        value={form.email} onChange={handleChange}
                        className="h-12 rounded-xl border-slate-200 focus-visible:ring-emerald-500 text-sm bg-slate-50"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="subject" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                      Sujet <span className="text-red-400 normal-case">*</span>
                    </Label>
                    <Input
                      id="subject" name="subject" required
                      placeholder="Informations sur l'adhésion"
                      value={form.subject} onChange={handleChange}
                      className="h-12 rounded-xl border-slate-200 focus-visible:ring-emerald-500 text-sm bg-slate-50"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="message" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                      Votre message <span className="text-red-400 normal-case">*</span>
                    </Label>
                    <Textarea
                      id="message" name="message" required
                      placeholder="Comment pouvons-nous vous aider ?"
                      value={form.message} onChange={handleChange}
                      className="rounded-xl border-slate-200 focus-visible:ring-emerald-500 min-h-[140px] resize-none text-sm bg-slate-50"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-slate-900 text-white hover:bg-emerald-800 rounded-full py-6 text-sm font-medium tracking-wide shadow-sm transition-all duration-300 disabled:opacity-60 group"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Envoi en cours…
                      </>
                    ) : (
                      <>
                        Envoyer le message
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer homeLink="/" contactLink="/contact" />
    </div>
  );
};
